import { useCallback, useEffect, useRef, useState } from "react";

import { recordingBreadcrumb } from "@/features/socratic/recording-diagnostics";
import {
  createSilentAudioNudgeTracker,
  NUDGE_STALE_MS,
} from "@/features/socratic/silent-audio-nudge-tracker";
import { useSampleTracker } from "@/hooks/use-sample-tracker";
import type { LevelReading } from "@/lib/audio-level-windows";

export interface SilentAudioNudgeState {
  /** Whether the "we can't hear you" nudge should be up right now. */
  visible: boolean;
  /**
   * Whether the nudge has been up at any point in this dialogue. Read when the
   * response is submitted: the nudge may well have cleared by then, and a nudge
   * followed by audible audio is a student who recovered.
   */
  wasShown: () => boolean;
  /** Hand to `useWebcamCapture` as `onLevelReading`. */
  onLevelReading: (reading: LevelReading) => void;
}

/**
 * Watches the microphone during a Socratic dialogue and decides when to tell the
 * student we cannot hear them.
 *
 * @param dialogueId The dialogue under way. Changing it starts the watch again,
 * so a reattempt is not judged on the previous attempt's silence.
 */
export function useSilentAudioNudge(
  dialogueId?: string
): SilentAudioNudgeState {
  const [visible, setVisible] = useState(false);
  // Compared against outside the state updater, so the breadcrumb is written
  // once per transition rather than once per call React makes of the updater.
  const visibleRef = useRef(false);
  const everShownRef = useRef(false);
  const staleTimerRef = useRef<number | null>(null);

  const cancelStaleTimer = useCallback(() => {
    if (staleTimerRef.current != null) {
      window.clearTimeout(staleTimerRef.current);
      staleTimerRef.current = null;
    }
  }, []);

  /**
   * Takes the nudge down. `everShown` survives: the student was shown it,
   * whatever happened next.
   */
  const hideNudge = useCallback(() => {
    cancelStaleTimer();
    visibleRef.current = false;
    setVisible(false);
  }, [cancelStaleTimer]);

  const { tracker, restart } = useSampleTracker(
    createSilentAudioNudgeTracker,
    dialogueId,
    hideNudge
  );

  const onLevelReading = useCallback(
    (reading: LevelReading) => {
      const shown = tracker().add(reading.level, reading.contextState);

      // Restarted on every reading, so it can only fire once the meter itself
      // has stopped producing them.
      cancelStaleTimer();
      if (shown) {
        staleTimerRef.current = window.setTimeout(restart, NUDGE_STALE_MS);
      }

      if (shown === visibleRef.current) return;
      visibleRef.current = shown;
      if (shown) {
        everShownRef.current = true;
        recordingBreadcrumb("nudge shown", { dialogue_id: dialogueId });
      }
      setVisible(shown);
    },
    [cancelStaleTimer, dialogueId, restart, tracker]
  );

  const wasShown = useCallback(() => everShownRef.current, []);

  useEffect(() => {
    everShownRef.current = false;
    return cancelStaleTimer;
  }, [dialogueId, cancelStaleTimer]);

  return { visible, wasShown, onLevelReading };
}
